import { isFieldViewModel } from "../../fields"
import { isArrayEmpty } from "../../utils/array"
import { FieldsMap } from "./fieldsMap"

export function getCroppingFeatures(map: FieldsMap, ids: number[]) {
    const features = ids.map((c) => map.croppingLayer.findCropping(c)).filter((c) => !!c)
    return features
}

/**
 * Amends an object to features used to manage the selection of cropping objects on the map
 *
 * @param target Target object
 * @param map Map
 * @returns Amended target object
 */
export function withCroppingSelection<T extends {}>(target: T, map: FieldsMap) {
    const selectionHandler = createSelectionHandler(map)

    return {
        ...target,
        ...selectionHandler,
    }
}

type InitSelection =
    | {
          canSelect: false
      }
    | {
          canSelect: true
          selectedCropping: number[]
          watcher(cropping: number[]): void
      }

function createSelectionHandler(map: FieldsMap) {
    initializeSelection({ canSelect: false })

    return {
        initializeSelection,

        setSelection,

        getSelection() {
            return map
                .getSelectedFeatures()
                .map((f) => f.get("data"))
                .filter(isFieldViewModel)
        },

        clearSelection() {
            map.clearSelection()
        },
    }

    function initializeSelection(init: InitSelection) {
        let dispose: () => void

        const { canSelect } = init

        map.fieldsLayer.selectable = false
        map.croppingLayer.selectable = canSelect
        map.enableMultipleSelection = canSelect

        if (!canSelect) {
            map.clearSelection()
        } else {
            const { selectedCropping, watcher } = init

            const features = setSelection(selectedCropping)

            const diposeWatcher = setupWatcher(map, watcher)

            dispose = () => {
                diposeWatcher && diposeWatcher()
                initializeSelection({ canSelect: false })
            }

            if (!isArrayEmpty(features)) {
                map.viewExtent({ items: features })
            }
        }

        return dispose
    }

    function setSelection(selection: number[]) {
        const features = getCroppingFeatures(map, selection)
        map.selectFeatures(features)
        return features
    }

    function setupWatcher(map: FieldsMap, watcher: (cropping: number[]) => void): () => void {
        const dispose = map.watchSelection((features) => {
            const selection = features
                .map((f) => f.get("data"))
                .filter(isFieldViewModel)
                .map((c) => c.croppingId)
                .filter((c) => !!c)
                .unique((c) => c)

            watcher && watcher(selection)
        })

        return dispose as () => void
    }
}
